'use client';

import React, { useMemo } from 'react';
import type { PermissionIdsType } from '@/consts/permissions';
import { usePermissions } from '@/context/PermissionsProvider';
import { useModalState } from '@/hooks/useModalState';
import { AccessRestrictedModal } from '../AccessRestrictedModal';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: PermissionIdsType | PermissionIdsType[];
  fallback?: React.ReactNode;
  requiredAllPermissions?: boolean;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  requiredAllPermissions,
  fallback = <NoAccess />,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  const hasAccess = useMemo(() => {
    if (Array.isArray(permission)) {
      return requiredAllPermissions ? hasAllPermissions(permission) : hasAnyPermission(permission);
    } else {
      return hasPermission((permission ?? '') as PermissionIdsType);
    }
  }, [permission, hasPermission, hasAnyPermission, hasAllPermissions, requiredAllPermissions]);

  return hasAccess ? children : fallback;
};

const NoAccess = () => {
  const { isOpen, close } = useModalState(true);

  return <AccessRestrictedModal isOpen={isOpen} onClose={close} />;
};